/**
 * UserMenu Component
 * Shows user avatar with dropdown menu and WebSocket status indicator
 */

import React, { useMemo } from 'react';
import { Avatar, Dropdown, Tooltip } from 'antd';
import { UserOutlined, LogoutOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { useWebSocketStatus } from '@/context/WebSocketContext';
import { useNavigate } from 'react-router-dom';
import { NotificationBell } from '@/components/features/notifications/NotificationBell';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import './UserMenu.css';

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

export const UserMenu: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { connected: wsConnected } = useWebSocketStatus();
  const { timezone } = useUserTimezone();
  const navigate = useNavigate();
  const [avatarError, setAvatarError] = React.useState(false);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user?.username) {
      return user.username.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Reset avatar error when user changes
  React.useEffect(() => {
    setAvatarError(false);
  }, [user?.id]);

  // Get timezone display with abbreviation and UTC offset
  const timezoneDisplay = useMemo(() => {
    const now = dayjs().tz(timezone);
    const abbreviation = now.format('z'); // e.g., "CET", "EST", "CST"
    const offsetMinutes = now.utcOffset();
    const offsetHours = Math.abs(offsetMinutes / 60);
    const offsetSign = offsetMinutes >= 0 ? '+' : '-';

    // Format offset as "+01:00" or "-05:00"
    const hours = Math.floor(offsetHours);
    const minutes = Math.abs(offsetMinutes % 60);
    const offsetStr = `${offsetSign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

    return `${abbreviation} (UTC${offsetStr})`;
  }, [timezone]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // User dropdown menu
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontWeight: 600 }}>
            {user?.first_name} {user?.last_name}
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>{user?.email}</div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
            @{user?.username}
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: 4 }}>
            Timezone: {timezoneDisplay}
          </div>
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: t('auth.logout'),
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <div className="user-menu-container">
      {/* WebSocket Status Indicator */}
      <Tooltip
        title={wsConnected ? 'WebSocket: Connected' : 'WebSocket: Disconnected'}
      >
        <span
          className={`user-menu-ws-indicator ${wsConnected ? 'ws-connected' : 'ws-disconnected'}`}
        />
      </Tooltip>

      {/* Notification Bell */}
      <NotificationBell />

      {/* User Avatar with Dropdown */}
      <Dropdown
        menu={{ items: userMenuItems }}
        placement="bottomRight"
        trigger={['click']}
      >
        <Avatar
          size={32}
          style={{
            backgroundColor: '#667eea',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
          }}
          src={!avatarError ? '/v2/api/tenants/me/avatar.jpg' : undefined}
          icon={(!user?.first_name || avatarError) && <UserOutlined />}
          onError={() => {
            setAvatarError(true);
            return false;
          }}
        >
          {user?.first_name && avatarError && getUserInitials()}
        </Avatar>
      </Dropdown>
    </div>
  );
};
